/**
 * Financial WebSocket Client
 * 
 * Connects to backend WebSocket for real-time financial updates.
 * Automatically reconnects on disconnect.
 */

class FinancialWebSocket {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
    this.reconnectTimeout = null;
    this.reconnectDelay = 3000;
    this.maxReconnectDelay = 30000;
    this.isConnecting = false;
  }

  /**
   * Connect to financial updates WebSocket
   */
  connect(token) {
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
      console.debug('WebSocket already connected or connecting');
      return;
    }

    this.isConnecting = true;
    
    // Construct WebSocket URL
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsUrl = `${protocol}//${backendUrl.replace(/^https?:\/\//, '')}/api/ws/financial${token ? `?token=${token}` : ''}`;

    console.debug('Connecting to financial WebSocket:', wsUrl);

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.debug('✅ Connected to financial updates stream');
        this.isConnecting = false;
        this.reconnectDelay = 3000; // Reset reconnect delay
        
        // Notify listeners
        this.emit('connection.established', { connected: true });
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.debug('📊 Financial event received:', message.event, message);
          
          // Emit to listeners
          this.emit(message.event, message.data || message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        this.isConnecting = false;
      };

      this.ws.onclose = () => {
        console.debug('⚠️  Disconnected from financial updates stream');
        this.isConnecting = false;
        this.ws = null;
        
        // Notify listeners
        this.emit('connection.closed', { connected: false });
        
        // Attempt to reconnect
        this.scheduleReconnect(token);
      };

    } catch (error) {
      console.error('Error creating WebSocket:', error);
      this.isConnecting = false;
      this.scheduleReconnect(token);
    }
  }

  /**
   * Schedule reconnection attempt
   */
  scheduleReconnect(token) {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    console.debug(`Reconnecting in ${this.reconnectDelay}ms...`);
    
    this.reconnectTimeout = setTimeout(() => {
      this.connect(token);
      // Exponential backoff
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay);
    }, this.reconnectDelay);
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.listeners.clear();
  }

  /**
   * Subscribe to an event
   * 
   * @param {string} eventType - Event type to listen for
   * @param {function} callback - Callback function
   * @returns {function} Unsubscribe function
   */
  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    
    this.listeners.get(eventType).add(callback);

    // Return unsubscribe function
    return () => this.off(eventType, callback);
  }

  /**
   * Unsubscribe from an event
   */
  off(eventType, callback) {
    if (this.listeners.has(eventType)) {
      this.listeners.get(eventType).delete(callback);
    }
  }

  /**
   * Emit event to listeners
   */
  emit(eventType, data) {
    if (this.listeners.has(eventType)) {
      this.listeners.get(eventType).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event listener for ${eventType}:`, error);
        }
      });
    }
  }

  /**
   * Send message to server
   */
  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not connected, cannot send message');
    }
  }

  /**
   * Send ping to keep connection alive
   */
  ping() {
    this.send({ action: 'ping' });
  }

  /**
   * Subscribe to specific order updates
   */
  subscribeToOrder(orderId) {
    this.send({
      action: 'subscribe',
      order_id: orderId
    });
  }

  /**
   * Check if connected
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

// Singleton instance
let financialWebSocket = null;

/**
 * Get or create financial WebSocket instance
 */
export const getFinancialWebSocket = () => {
  if (!financialWebSocket) {
    financialWebSocket = new FinancialWebSocket();
  }
  return financialWebSocket;
};

/**
 * React hook for financial WebSocket
 */
export const useFinancialWebSocket = (token) => {
  const ws = getFinancialWebSocket();
  
  React.useEffect(() => {
    if (token) {
      ws.connect(token);
    }
    
    return () => {
      // Don't disconnect on unmount, keep connection alive
      // ws.disconnect();
    };
  }, [token, ws]);
  
  return ws;
};

export default getFinancialWebSocket;
